import { ArrowLeft, ArrowUpRight, BarChart3, Bell, Bold, BookmarkPlus, CheckCircle, ChevronLeft, ChevronRight, Circle as CircleIcon, Clock3, Crop, Download, Euro, Eye, EyeOff, FileText, Folder, GitBranch, GitMerge, History, ImagePlus, Italic, MessageSquare, Pencil, Plus, RefreshCw, RotateCcw, RotateCw, Search, Sparkles, Square, Trash2, Type, Undo2, Upload, Users, Wand2, X, Zap } from "lucide-react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { Link, useParams } from "react-router-dom";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, streamGuttenbergEvents } from "./api";
import { useAuth } from "../Auth";
import "./Guttenberg.css";
import { aiModeLabel, aiProviderLabel, branchScopedLocks, buildRichPlanSections, collaborationColor, emptyAnalysisDraft, emptyCollaborationState, eventTargetsBranch, extractOutlinePreview, formatTargetPages, getCollaborationDisplayName, getCollaborationSessionId, getStoredActiveBranchId, hasGeneratedReport, imageProcessingKey, isOutlineInstruction, jobActivityDetail, mergeDriveFilePage, mergeJobTrace, nextClarificationQuestion, nextDriveBreadcrumbs, normalizeAiMode, normalizeAiModes, normalizeAiProviders, normalizeBranchId, normalizeOutlinePreview, parseTargetPages, pendingClarificationQuestions, reportAssistantActionInstructions, reportAssistantActionKey, reportAssistantChangeTargets, sourcePathLabel, sourceStatusLabel, storeActiveBranchId, toAnalysisDraft, uploadSourceBatch, visualAssetSourceKey, visualReviewIsCurrent } from "./GuttenbergPageUtils";
import { AiActivityPanel, BranchPanel, CollaborationStatus, CostPanel, DriveSourcePicker, ImageCropModal, PlanReviewPanel, ReadOnlyReportPage, WorkflowStepper } from "./GuttenbergWorkflowPanels";
import { AnalysisPanel, ClarificationQuestionModal, ImageBankPanel, ReportContextPanel } from "./GuttenbergAnalysisPanels";
import { CommentSuggestionDiff, FullReportPreview, OutlineLivePreview, ReportAttributionToggle, ReportOutlinePanel, UserInputQueue, VersionPanel } from "./GuttenbergReportPreview";
import { AUTO_RETOUCH_INSTRUCTION, chartCommentTitle, contextImageSrc, defaultImageCaption, pathCaptionFallback, reportImageSrc } from "./GuttenbergImageAndDiff";
import { changeTypeLabel, chartCommentTargetText, chartTitle, cssEscape, insertMarkdownBlockAfterIndex, replaceMarkdownBlockByIndex } from "./GuttenbergChartMarkdown";

export function GuttenbergProjectView({
    acceptSuggestion,
    activeAssistantReview,
    activeBranchId,
    activeClarificationQuestion,
    activeReportAssistantThreadId,
    activeReportTool,
    activeVersion,
    activityClock,
    aiMode,
    aiProvider,
    aiProviderBusy,
    aiProviderInfo,
    analysis,
    analysisDraft,
    analysisRunning,
    analyzeProject,
    resetAnalysis,
    answerClarificationQuestion,
    appliedReportAssistantActions,
    applyImageCrop,
    applyOpenComments,
    applyReportAssistantAction,
    approveOutline,
    attributionSaving,
    autoRetouchImage,
    blurEditableBlock,
    branchName,
    branchState,
    browserNotificationPermission,
    busy,
    cancelCurrentJob,
    changeAiMode,
    changeAiProvider,
    chatMessage,
    clarificationAnswerBusy,
    clarificationQuestions,
    clearDriveSearch,
    closeAssistantReview,
    collaboration,
    collaborationSessionRef,
    commentAnchor,
    commentDraft,
    commentMode,
    commentReplies,
    contextDraft,
    contextPanelOpen,
    contextSaving,
    contextUploadBusy,
    costs,
    createBranch,
    createMilestone,
    createReportSection,
    deletePreviewBlock,
    deleteReportSection,
    dismissComment,
    driveBreadcrumbs,
    driveFiles,
    driveLoading,
    driveQuery,
    driveSelection,
    editMode,
    enableBrowserQuestionNotifications,
    exportFile,
    exportRunning,
    fastMode,
    fastModeBusy,
    fastModeInfo,
    focusEditableBlock,
    focusReportComment,
    generateOutline,
    imageEditTarget,
    importSelectedDriveFiles,
    insertManualImageFromComposer,
    insertPreviewBlock,
    insertSelectedBankImageFromComposer,
    intakeEvents,
    isBlockingJob,
    isImageEditJob,
    job,
    jobTrace,
    liveSections,
    load,
    mainTab,
    manualImageCaption,
    manualImageFile,
    manualImageNote,
    mergeBranch,
    mergeFeedback,
    milestoneLabel,
    navigateAssistantReview,
    openDriveBreadcrumb,
    openDriveFolder,
    openImageCropEditor,
    openReportAssistantComposer,
    openReportAssistantImageBank,
    outlineApprovalPending,
    outlineDraft,
    outlineInstruction,
    outlinePreview,
    outlineRunning,
    pendingComment,
    prepareVisuals,
    processingCommentIds,
    processingImageKeys,
    processingSectionIds,
    project,
    projectId,
    refineOutline,
    refreshDriveFiles,
    rejectSuggestion,
    replaceImageWithBank,
    replaceImageWithUpload,
    report,
    reportAssistantThinking,
    reportInstruction,
    reportInstructionBankImages,
    reportInstructionFiles,
    reportInstructionImages,
    reportLivePhase,
    reportOutlineCollapsed,
    resetManualImageDraft,
    resetReportAssistant,
    restoreBlockVersion,
    restoreMilestone,
    restoreSectionVersion,
    reviewAssistantMessageChanges,
    run,
    runFinalReview,
    saveAnalysis,
    saveContextPanel,
    savePreviewBlock,
    savingSectionIds,
    searchDriveFiles,
    selectChartForComment,
    selectImageForComment,
    selectImageForEdit,
    selectedBankImage,
    sendChat,
    setActiveClarificationQuestion,
    setActiveReportTool,
    setActiveVersion,
    setBranchName,
    setChatMessage,
    setCommentAnchor,
    setCommentDraft,
    setCommentMode,
    setCommentReplies,
    setContextPanelOpen,
    setDriveQuery,
    setEditMode,
    setImageEditTarget,
    setMainTab,
    setManualImageCaption,
    setManualImageFile,
    setManualImageNote,
    setMergeFeedback,
    setMilestoneLabel,
    setOutlineInstruction,
    setPendingComment,
    setReportInstruction,
    setReportInstructionFiles,
    setReportInstructionImages,
    setReportOutlineCollapsed,
    setSelectedBankImage,
    setWorkflowCollapsed,
    startAnchoredComment,
    startBankImageDiscussion,
    startGeneration,
    startInsertionComment,
    status,
    submitAndTreatComment,
    submitComment,
    submitCommentReply,
    submitOutlineInstruction,
    submitReportInstruction,
    suggestComment,
    suggestOpenComments,
    switchBranch,
    syncBrief,
    targetPages,
    titleDraft,
    toggleDriveFile,
    toggleGuttenbergAttribution,
    toggleReportAssistantBankImage,
    updateAnalysisDraft,
    updateContextDraft,
    updateTargetPagesDraft,
    updateTextCommentAnchor,
    updateTitleDraft,
    updateVisualAssetLabel,
    updateVisualAssetStatus,
    uploadClientLogo,
    uploadFiles,
    uploadLiveContextFiles,
    workflowCollapsed,
}) {
    const progress = job?.progress_total ? Math.round((job.progress_current / job.progress_total) * 100) : 0;

    const jobInProgress = job?.status === "queued" || job?.status === "running";

    const activityDetail = jobActivityDetail(job, activityClock);

    const imageEditJobInProgress = jobInProgress && isImageEditJob(job);

    const blockingJobInProgress = isBlockingJob(job);

    const visualPreparationRunning = jobInProgress && job?.type === "prepare-visuals";

    const analysisStepRunning = analysisRunning || (jobInProgress && job?.type === "prepare-analysis");

    const operationInProgress = busy || analysisStepRunning || jobInProgress || project.intake_status === "running";

    const canGenerate = !operationInProgress && project.source_files.length > 0;

    const outlineApproved = !!analysis?.outline_approved;

    const outlineStage = analysis?.outline_stage ?? (outlineApproved ? "approved" : "none");

    const outlineStale = outlineStage === "stale";

    const previewSections = report ? { ...report.sections, ...liveSections } : liveSections;

    const reviewIssues = (report?.manifest.review_issues ?? []).filter((issue) => !issue.resolved);

    const factReviewLedger = analysis?.fact_review_ledger ?? {};

    const factualCoverage = factReviewLedger.coverage ?? {};

    const factualBatches = factReviewLedger.batches ?? [];

    const comments = report?.manifest.comments ?? [];

    const sectionVersions = [...(report?.manifest.section_versions ?? [])].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));

    const activeAssistantReviewTargets = reportAssistantChangeTargets(activeAssistantReview?.action, sectionVersions);

    const activeAssistantReviewIndex = activeAssistantReviewTargets.length
                ? Math.min(activeAssistantReview?.index ?? 0, activeAssistantReviewTargets.length - 1)
                : 0;

    const assistantReviewVersionIds = Array.isArray(activeAssistantReview?.action?.version_ids)
                ? activeAssistantReview.action.version_ids
                : [];

    const milestones = [...(report?.manifest.milestones ?? [])].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));

    const visibleComments = comments.filter((comment) => comment.status !== "applied" && comment.status !== "dismissed");

    const openComments = comments.filter((comment) => comment.status === "open" || comment.status === "suggestion");

    const pendingValidationComments = visibleComments.filter((comment) => comment.status === "suggestion" &&
                comment.edit_suggestion?.status === "ready");

    const actionableReviewComments = visibleComments.filter((comment) => comment.source === "review" &&
                !!comment.section_id &&
                (comment.status === "open" || comment.status === "suggestion"));

    const blockingReviewIssues = reviewIssues.filter((issue) => issue.severity === "error" && !issue.resolved);

    const hasBlockingReviewIssues = blockingReviewIssues.length > 0;

    const pendingReviewQuestions = clarificationQuestions.filter((question) => question.stage === "review_resolution");

    const pendingAnalysisQuestions = clarificationQuestions.filter((question) => question.stage !== "review_resolution");

    const reviewNeedsResume = report?.manifest.status === "review_incomplete";

    const showOutlinePreview = reportLivePhase === "planning" && (outlineRunning || outlinePreview.length > 0);

    const visualAssetById = new Map((analysis?.visual_assets ?? []).map((asset) => [asset.id, asset]));

    const outlineReviewSections = buildRichPlanSections(analysis, report?.manifest, visualAssetById);

    const outlineExists = ["draft", "stale", "approved"].includes(outlineStage) && outlineReviewSections.length > 0;

    const hasAnalysisArtifacts = Boolean(analysis &&
                ((analysis.analysis_pipeline_stage && !["idle", "intake"].includes(analysis.analysis_pipeline_stage)) ||
                    analysis.analysis_brief.trim() ||
                    analysis.report_intent.objective.trim() ||
                    analysis.findings.length ||
                    analysis.useful_facts.length ||
                    analysis.data_insights?.length ||
                    analysis.generated_artifacts?.length ||
                    analysis.analysis_tasks?.length ||
                    analysis.canonical_metrics?.length));

    const visualInventoryReady = analysis?.visual_inventory_status === "ready";

    const visualInventoryStale = Boolean(
                analysis?.visual_assets?.length && !visualInventoryReady && !visualPreparationRunning
            );

    const analysisUsesCurrentVisuals = Boolean(
                hasAnalysisArtifacts
                && visualReviewIsCurrent(analysis)
            );

    const currentPendingAnalysisQuestions = analysisUsesCurrentVisuals ? pendingAnalysisQuestions : [];

    const canGenerateOutline = !operationInProgress && project.report_ready && analysisUsesCurrentVisuals;

    const outlineBlockedTitle = !visualInventoryReady
                ? "Prépare les images avant l'analyse"
                : !analysisUsesCurrentVisuals
                    ? "Lance l'analyse avec les choix visuels actuels avant le plan"
                    : !project.report_ready
                        ? "Termine les questions bloquantes avant le plan"
                        : undefined;

    const hasWrittenReport = Boolean(report?.manifest.sections.some((section) => {
                if (["generated", "reviewed", "edited"].includes(section.status))
                    return true;
                const markdown = previewSections[section.id] ?? "";
                if (/Redaction en attente/i.test(markdown))
                    return false;
                return markdown.replace(/[#*_`\s]/g, "").length > 180;
            }));

    const canExport = !exportRunning && hasWrittenReport;

    const exportHasQualityWarnings = Boolean(
                pendingReviewQuestions.length || reviewNeedsResume || openComments.length || reviewIssues.length
            );

    const hasBlockedWritingSection = Boolean(report?.manifest.sections.some((section) => {
                if (section.status === "blocked")
                    return true;
                const markdown = previewSections[section.id] ?? "";
                return /BLOCAGE REDACTION/i.test(markdown);
            }));

    const generationNeedsRetry = Boolean(
                report?.manifest.status === "generation_failed" ||
                hasBlockedWritingSection ||
                (job?.type === "generate-report" && job?.status === "failed")
            );

    const hasReviewRound = Boolean((analysis?.review_rounds.length ?? 0) > 0 || reviewIssues.length || ["ship_ready", "review_incomplete", "awaiting_clarification"].includes(report?.manifest.status));

    const processingCommentCount = Object.keys(processingCommentIds).length;

    const processingSectionCount = Object.keys(processingSectionIds).length;

    const visualEditingLocked = visualPreparationRunning || analysisStepRunning || project.intake_status === "running";

    const outlineStepRunning = outlineRunning || reportLivePhase === "planning" || (jobInProgress && job?.type === "generate-outline");

    const selectedDriveCount = Object.keys(driveSelection).length;

    const visualPortfolioCount = (analysis?.visual_assets ?? []).filter((asset) => ["selected", "required"].includes(asset.editorial_status) || ["required", "used"].includes(asset.status)).length;

    const visualCandidateCount = analysis?.visual_assets?.length ?? 0;

    const projectChatMessages = (project.chat_messages ?? []).filter((message) => (message.scope ?? "project") === "project");

    const reportChatMessages = (project.chat_messages ?? []).filter((message) => message.scope === "report");

    const reportAssistantThreads = [...(project.report_assistant_threads ?? [])].sort((a, b) => Date.parse(b.updated_at || b.created_at) - Date.parse(a.updated_at || a.created_at));

    const activeReportAssistantThread = reportAssistantThreads.find((thread) => thread.id === activeReportAssistantThreadId) ?? reportAssistantThreads[0] ?? null;

    const activeReportChatMessages = reportChatMessages.filter((message) => !activeReportAssistantThread?.id || message.thread_id === activeReportAssistantThread.id);

    const reportAssistantHasDraft = Boolean(reportInstruction.trim() || reportInstructionImages.length || reportInstructionFiles.length || reportInstructionBankImages.length);

    const imageComposerSaving = pendingComment?.sectionId ? Boolean(savingSectionIds[pendingComment.sectionId]) : false;

    const imageCropBusy = imageEditTarget ? Boolean(processingImageKeys[imageProcessingKey(imageEditTarget.sectionId, imageEditTarget.imagePath)]) : false;

    const writingStepRunning = reportLivePhase === "writing" || (jobInProgress && job?.type === "generate-report");

    const writingProgressDetail = writingStepRunning
                ? job?.progress_total
                    ? `${job.message || job.current_step || "Redaction en cours"} (${job.progress_current}/${job.progress_total}) · ${activityDetail}`
                    : `${job?.message || status || "Preparation de la redaction"}${activityDetail ? ` · ${activityDetail}` : ""}`
                : "";

    const reviewStepRunning = processingCommentCount > 0 ||
                processingSectionCount > 0 ||
                (jobInProgress && (job?.type === "comment-suggestion" || job?.type === "apply-comments" || job?.type === "report-instruction"));

    const currentSessionId = collaborationSessionRef.current;

    const collaborationParticipants = collaboration?.participants ?? [];

    const collaborationLocks = branchScopedLocks(collaboration?.locks ?? [], activeBranchId);

    const reportOperationLocked = blockingJobInProgress || analysisStepRunning || project.intake_status === "running";

    const imageJobBranchId = normalizeBranchId(job?.result?.branch_id || activeBranchId);

    const localImageJobLock = imageEditJobInProgress &&
                imageJobBranchId === normalizeBranchId(activeBranchId) &&
                job?.result?.section_id &&
                !collaborationLocks.some((lock) => lock.job_id === job.id)
                ? {
                    id: "local-active-image-operation",
                    kind: "ai",
                    scope: "image",
                    branch_id: imageJobBranchId,
                    section_id: job.result.section_id,
                    image_path: job.result.image_path ?? "",
                    job_id: job.id,
                    job_type: job.type,
                    label: job.message || "Retouche image en cours"
                }
                : null;

    const effectiveCollaborationLocks = [
                ...collaborationLocks,
                ...(localImageJobLock ? [localImageJobLock] : []),
                ...(reportOperationLocked
                    ? [{
                        id: "local-active-operation",
                        kind: "ai",
                        scope: "report",
                        branch_id: activeBranchId,
                        label: job?.message || (analysisStepRunning ? "Analyse du dossier en cours" : "Operation Guttenberg en cours")
                    }]
                    : [])
            ];

    const otherParticipants = collaborationParticipants.filter((participant) => participant.session_id !== currentSessionId);

    const activeBranch = branchState?.branches?.find((branch) => branch.id === activeBranchId) ?? null;

    const workflowSteps = [
                {
                    id: "sources",
                    title: "1. Sources & brief",
                    detail: project.source_files.length
                        ? `${project.source_files.length} fichier(s) · ${contextDraft.trim() ? "brief renseigné" : "brief à préciser"}`
                        : "Ajoute les sources et précise l'objectif du rapport",
                    status: project.source_files.length && contextDraft.trim() ? "done" : "active",
                    action: { label: "Ajouter sources", kind: "secondary", disabled: operationInProgress },
                    secondaryAction: {
                        label: "Modifier brief",
                        onClick: () => setMainTab("analysis"),
                        kind: "secondary",
                        disabled: operationInProgress
                    }
                },
                {
                    id: "images",
                    title: "2. Images",
                    detail: visualPreparationRunning
                        ? "Extraction des images intégrées aux sources"
                        : visualInventoryReady
                            ? `${visualPortfolioCount} visuel(s) retenu(s) sur ${visualCandidateCount}; prêt pour l'analyse`
                            : visualInventoryStale
                                ? "Les sources ont changé; actualise la bibliothèque avant l'analyse"
                                : "Extrais les images pour les sélectionner et les annoter avant l'analyse",
                    status: visualPreparationRunning ? "running" : visualInventoryReady ? "done" : project.source_files.length ? "active" : "locked",
                    action: {
                        label: visualPreparationRunning
                            ? "Extraction..."
                            : visualInventoryReady
                                ? "Ajuster images"
                                : visualInventoryStale
                                    ? "Actualiser images"
                                    : "Extraire images",
                        onClick: visualInventoryReady ? () => setMainTab("analysis") : () => prepareVisuals(),
                        kind: visualInventoryReady ? "secondary" : "primary",
                        disabled: operationInProgress || project.source_files.length === 0,
                        title: project.source_files.length === 0 ? "Ajoute au moins une source" : undefined
                    }
                },
                {
                    id: "analysis",
                    title: "3. Analyse & questions",
                    detail: analysisStepRunning
                        ? intakeEvents.slice(-1)[0] ?? "Analyse en cours"
                        : currentPendingAnalysisQuestions.length
                            ? `${currentPendingAnalysisQuestions.length} question(s) à traiter après l'analyse`
                            : analysisUsesCurrentVisuals
                                ? "Dossier d'analyse disponible"
                                : visualInventoryReady
                                    ? "Lance l'analyse sur les sources et les choix visuels"
                                    : "Prépare d'abord la bibliothèque d'images",
                    status: analysisStepRunning
                        ? "running"
                        : currentPendingAnalysisQuestions.length
                            ? "active"
                            : analysisUsesCurrentVisuals
                                ? "done"
                                : visualInventoryReady
                                    ? "active"
                                    : "locked",
                    action: currentPendingAnalysisQuestions.length
                        ? {
                            label: "Répondre",
                            onClick: () => {
                                setMainTab("analysis");
                                setActiveClarificationQuestion(currentPendingAnalysisQuestions[0]);
                            },
                            kind: "primary",
                            disabled: operationInProgress
                        }
                        : {
                            label: hasAnalysisArtifacts ? "Reprendre analyse" : "Analyser",
                            onClick: () => {
                            setMainTab("analysis");
                                analyzeProject();
                            },
                            kind: hasAnalysisArtifacts ? "secondary" : "primary",
                            disabled: operationInProgress || !visualInventoryReady,
                            title: !visualInventoryReady ? "Extrais et relis les images avant l'analyse" : undefined
                        }
                },
                {
                    id: "outline",
                    title: "4. Plan",
                    detail: outlineApproved
                        ? "Plan valide"
                        : outlineStepRunning
                            ? "Agent en train de preparer le plan"
                            : outlineStale
                                ? "Choix d'images modifie : regenere le plan"
                            : outlineExists
                                ? "Plan a relire et valider"
                                : "Le plan sera généré automatiquement après les questions",
                    status: outlineStepRunning ? "running" : outlineApproved ? "done" : outlineExists ? "active" : analysisUsesCurrentVisuals ? "active" : "locked",
                    action: outlineExists && !outlineApproved && !outlineStale
                        ? {
                            label: outlineApprovalPending ? "Validation..." : "Valider plan",
                            onClick: approveOutline,
                            disabled: operationInProgress || outlineApprovalPending,
                            title: "Valider le plan visible dans la preview"
                        }
                        : job?.type === "generate-outline" && job?.status === "failed"
                            ? {
                                label: "Reprendre plan",
                                onClick: generateOutline,
                                disabled: !canGenerateOutline,
                                title: outlineBlockedTitle
                            }
                            : undefined,
                    secondaryAction: outlineExists && !outlineStale
                        ? {
                            label: "Voir plan",
                            onClick: () => setMainTab("report"),
                            kind: "secondary",
                            disabled: operationInProgress
                        }
                        : undefined
                },
                {
                    id: "writing",
                    title: "5. Redaction",
                    detail: writingStepRunning
                        ? writingProgressDetail
                        : generationNeedsRetry
                            ? "La derniere redaction s'est interrompue; les sections deja enregistrees seront conservees"
                            : hasWrittenReport
                                ? "Rapport redige"
                                : "Rédige le rapport après validation du plan",
                    status: writingStepRunning
                        ? "running"
                        : generationNeedsRetry
                            ? "warning"
                            : hasWrittenReport
                                ? "done"
                                : outlineApproved
                                    ? "active"
                                    : "locked",
                    action: {
                        label: generationNeedsRetry ? "Reprendre" : hasWrittenReport ? "Rapport redige" : "Rediger",
                        onClick: startGeneration,
                        disabled: (!generationNeedsRetry && hasWrittenReport) || !canGenerate || !outlineApproved,
                        title: !outlineApproved
                            ? "Valide le plan avant la rédaction"
                            : generationNeedsRetry
                                ? "Relancer la rédaction en conservant les versions déjà enregistrées"
                                : undefined
                    }
                },
                {
                    id: "review",
                    title: "6. Corrections",
                    detail: reviewStepRunning
                        ? processingCommentCount + processingSectionCount > 0
                            ? `${processingCommentCount + processingSectionCount} traitement(s) IA en cours`
                            : "Traitement IA en cours"
                        : pendingReviewQuestions.length
                            ? `${pendingReviewQuestions.length} reponse(s) requise(s) avant livraison`
                        : reviewNeedsResume
                            ? "La relecture s'est interrompue; elle reprendra sans regenerer le rapport"
                        : openComments.length
                            ? `${openComments.length} commentaire(s) ouvert(s)`
                            : hasReviewRound
                                ? "Relecture effectuee"
                                : "Relis, commente et lance la relecture",
                    status: reviewStepRunning
                        ? "running"
                        : pendingReviewQuestions.length || reviewNeedsResume || openComments.length || blockingReviewIssues.length
                            ? "warning"
                            : hasReviewRound
                                ? "done"
                                : hasWrittenReport
                                    ? "active"
                                    : "locked",
                    action: pendingReviewQuestions.length
                        ? {
                            label: "Repondre",
                            onClick: () => setActiveClarificationQuestion(pendingReviewQuestions[0]),
                            disabled: operationInProgress,
                            kind: "secondary"
                        }
                        : openComments.length
                        ? {
                            label: "Traiter commentaires",
                            onClick: suggestOpenComments,
                            disabled: operationInProgress,
                            kind: "secondary"
                        }
                        : {
                            label: "Relecture",
                            onClick: runFinalReview,
                            disabled: operationInProgress || !hasWrittenReport,
                            kind: "secondary"
                        }
                },
                {
                    id: "export",
                    title: "7. Export",
                    detail: canExport
                        ? exportHasQualityWarnings
                            ? "PDF/DOCX disponibles avec points à vérifier"
                            : "PDF/DOCX disponibles"
                        : "Disponible dès qu'une première version du rapport est rédigée",
                    status: canExport ? (exportHasQualityWarnings ? "warning" : "active") : "locked",
                    action: {
                        label: "PDF",
                        onClick: () => exportFile("pdf"),
                        disabled: !canExport,
                        title: canExport ? "Exporter en PDF" : "Aucun contenu rédigé à exporter"
                    },
                    secondaryAction: {
                        label: "DOCX",
                        onClick: () => exportFile("docx"),
                        disabled: !canExport,
                        kind: "secondary",
                        title: canExport ? "Exporter en DOCX" : "Aucun contenu rédigé à exporter"
                    }
                }
            ];

    const showPlanReview = mainTab === "report" && !outlineApproved && outlineExists && !outlineStale && !showOutlinePreview;

    const inputAttentionCount = pendingValidationComments.length + clarificationQuestions.length + (showPlanReview ? 1 : 0);

    const allClarificationQuestions = analysis?.clarification_questions ?? [];

    const answeredClarificationQuestions = allClarificationQuestions.filter((question) => question.status !== "pending");

    function questionVisuals(question) {
                const ids = new Set(question?.visual_asset_ids ?? []);
                return (analysis?.visual_assets ?? []).filter((asset) => ids.has(asset.id) && asset.path);
            }

    const assistantToolContent = showPlanReview ? (<section className="panel">
                <h2>Chat plan</h2>
                <div className="chat-log plan-chat-log">
                  <div className="chat-message agent">
                    Demande les modifications du plan ici avant validation. L'agent mettra a jour les sections, briefs et pages estimees.
                  </div>
                  {outlineRunning && (<div className="chat-message agent live">
                      Modification du plan en cours...
                    </div>)}
                </div>
                <form className="report-chat-form" onSubmit={submitOutlineInstruction}>
                  <textarea rows={4} value={outlineInstruction} onChange={(event) => setOutlineInstruction(event.target.value)} placeholder="Ex: ajoute une section limites, fusionne methodologie et resultats, reduis a 12 pages..."/>
                  <button className="button" disabled={busy || outlineRunning || !outlineInstruction.trim()} type="submit">
                    <MessageSquare size={17}/> Modifier le plan
                  </button>
                </form>
              </section>) : (<section className="panel">
                <div className="section-heading">
                  <h2>Assistant rapport</h2>
                  <button className="button secondary compact-button" type="button" disabled={operationInProgress || reportAssistantThinking} onClick={resetReportAssistant}>
                    <Trash2 size={15}/> Reinitialiser
                  </button>
                </div>
                <div className="chat-log report-assistant-log">
                  {activeReportChatMessages.map((message) => {
                    const reviewTargets = message.action?.kind === "show_report_changes"
                        ? reportAssistantChangeTargets(message.action, sectionVersions)
                        : [];
                    const reviewActive = activeAssistantReview?.messageId === message.id;
                    const currentReviewTarget = reviewActive ? activeAssistantReviewTargets[activeAssistantReviewIndex] : null;
                    return (<div className={`chat-message ${message.role} ${reviewActive ? "assistant-review-active" : ""}`} key={message.id}>
                      <div className="chat-message-text">{message.content}</div>
                      {message.action?.kind === "apply_report_instruction" && (<div className="message-actions">
                          <span className="muted">
                            Mode : {message.action.change_scope === "structure" ? "modifier la structure du plan puis les sections concernées" : "modifier uniquement le contenu existant"}
                          </span>
                          {Array.isArray(message.action?.target_section_ids) && message.action.target_section_ids.length > 0 && (<span className="muted">
                              Portee : {message.action.target_section_ids.map((sectionId) => report?.manifest?.sections?.find((section) => section.id === sectionId)?.title || sectionId).join(", ")}
                            </span>)}
                          <button className="button compact-button" type="button" disabled={operationInProgress || Boolean(appliedReportAssistantActions[reportAssistantActionKey(message.action)])} onClick={() => applyReportAssistantAction(message.action)}>
                            <Wand2 size={16}/> {appliedReportAssistantActions[reportAssistantActionKey(message.action)] ? "Deja envoye" : message.action.label || "Appliquer au rapport"}
                          </button>
                        </div>)}
                      {message.action?.kind === "show_report_changes" && reviewTargets.length > 0 && (<div className="message-actions">
                          <button className={`button secondary compact-button assistant-review-button ${reviewActive ? "active" : ""}`} type="button" onClick={() => reviewAssistantMessageChanges(message)}>
                            <History size={16}/> {message.action.label || "Voir les modifications"} ({reviewTargets.length})
                          </button>
                        </div>)}
                      {reviewActive && currentReviewTarget && (<div className="assistant-change-navigator" aria-label="Navigation dans les modifications de l'assistant">
                          <button className="button secondary compact-button" type="button" aria-label="Modification precedente" disabled={activeAssistantReviewIndex === 0} onClick={() => navigateAssistantReview(message, -1)}>
                            <ChevronLeft size={16}/>
                          </button>
                          <span>
                            <strong>Modification {activeAssistantReviewIndex + 1} / {activeAssistantReviewTargets.length}</strong>
                            <small>{currentReviewTarget.sectionTitle} - {changeTypeLabel(currentReviewTarget.changeType)}</small>
                          </span>
                          <button className="button secondary compact-button" type="button" aria-label="Modification suivante" disabled={activeAssistantReviewIndex >= activeAssistantReviewTargets.length - 1} onClick={() => navigateAssistantReview(message, 1)}>
                            <ChevronRight size={16}/>
                          </button>
                          <button className="button secondary compact-button assistant-review-close" type="button" aria-label="Fermer la revue des modifications" title="Masquer les modifications" onClick={closeAssistantReview}>
                            <X size={16}/>
                          </button>
                        </div>)}
                    </div>);
                })}
                  {reportAssistantThinking && (<div className="chat-message agent live">
                      L'assistant consulte le rapport...
                    </div>)}
                  {!activeReportChatMessages.length && (<div className="muted">Pose une question sur le rapport ou demande une modification. Les changements seront proposes avant application.</div>)}
                </div>
                <form className="report-chat-form" onSubmit={submitReportInstruction}>
                  <textarea rows={4} value={reportInstruction} onChange={(event) => setReportInstruction(event.target.value)} placeholder="Question ou demande de modification a confirmer..."/>
                  <div className="message-attachments">
                    <div className="message-attachment-actions">
                      <label className="dropzone compact-dropzone report-image-upload">
                        <ImagePlus size={16}/>
                        <span>Joindre des images</span>
                        <input hidden type="file" accept="image/*" multiple onChange={(event) => {
                      const files = Array.from(event.target.files ?? []);
                      if (files.length)
                          setReportInstructionImages((current) => [...current, ...files]);
                      event.target.value = "";
                  }}/>
                      </label>
                      <button className="dropzone compact-dropzone report-image-upload" type="button" onClick={openReportAssistantImageBank}>
                        <BookmarkPlus size={16}/>
                        <span>Banque{reportInstructionBankImages.length ? ` (${reportInstructionBankImages.length})` : ""}</span>
                      </button>
                      <label className="dropzone compact-dropzone report-image-upload">
                        <FileText size={16}/>
                        <span>Joindre un fichier</span>
                        <input hidden type="file" multiple onChange={(event) => {
                      const files = Array.from(event.target.files ?? []);
                      if (files.length)
                          setReportInstructionFiles((current) => [...current, ...files]);
                      event.target.value = "";
                  }}/>
                      </label>
                    </div>
                    {!!reportInstructionBankImages.length && (<div className="report-bank-attachment-grid">
                        {reportInstructionBankImages.map((image) => (<figure key={image.path}>
                            <img src={reportImageSrc(projectId, image.path)} alt={image.caption || image.path}/>
                            <figcaption>{image.caption || image.path}</figcaption>
                            <button type="button" onClick={() => toggleReportAssistantBankImage(image)} title="Retirer cette image du message" aria-label="Retirer cette image du message">
                              <X size={14}/>
                            </button>
                          </figure>))}
                      </div>)}
                    {!!(reportInstructionImages.length || reportInstructionFiles.length) && (<div className="attachment-chips">
                        {reportInstructionImages.map((file, index) => (<button key={`${file.name}-${index}`} type="button" onClick={() => setReportInstructionImages((current) => current.filter((_, itemIndex) => itemIndex !== index))} title="Retirer cette image du message">
                            {file.name}
                            <X size={13}/>
                          </button>))}
                        {reportInstructionFiles.map((file, index) => (<button className="file" key={`${file.name}-${index}`} type="button" onClick={() => setReportInstructionFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))} title="Retirer ce fichier du message">
                            Fichier: {file.name}
                            <X size={13}/>
                          </button>))}
                      </div>)}
                  </div>
                  <button className="button" disabled={operationInProgress || reportAssistantThinking || !reportAssistantHasDraft} type="submit">
                    <MessageSquare size={17}/> Envoyer
                  </button>
                </form>
              </section>);

    const activityToolContent = job || jobTrace.length ? (<AiActivityPanel job={job} jobInProgress={jobInProgress} progress={progress} status={status} trace={jobTrace} activityDetail={activityDetail} onCancel={cancelCurrentJob}/>) : (<section className="panel report-tool-empty">
                <h2>Activite IA</h2>
                <p className="muted">Aucune operation IA en cours.</p>
              </section>);

    const commentsToolContent = (<section className="panel comments-panel">
                <div className="section-heading">
                  <h2>Commentaires</h2>
                  <button disabled={busy || openComments.length === 0} className="button secondary compact-button" onClick={suggestOpenComments} title="Preparer des suggestions pour les commentaires ouverts">
                    <CheckCircle size={16}/> Suggestions
                  </button>
                </div>
                {visibleComments.length ? (<ul className="comment-list">
                  {visibleComments.map((comment) => {
                    const isProcessing = Boolean(processingCommentIds[comment.id] || comment.edit_suggestion?.status === "pending");
                    return (<li key={comment.id} className={`${comment.status} ${comment.author === "review_agent" ? "agent" : ""} ${isProcessing ? "processing" : ""}`}>
                      <div className="comment-meta">
                        <strong>{comment.author === "review_agent" ? "Relecture" : "Utilisateur"}</strong>
                        <span>{comment.section_id ?? "Rapport"}</span>
                      </div>
                      {comment.target_type === "chart"
                        ? <div className="muted">Graphe cible: {chartCommentTitle(comment.selected_text)}</div>
                        : comment.selected_text && <blockquote>{comment.selected_text}</blockquote>}
                      {comment.image_path && <div className="muted">{comment.image_path}</div>}
                      <p>{comment.comment}</p>
                      {!!comment.attachments?.length && (<div className="comment-attachments">
                          {comment.attachments.map((attachment) => (<figure key={attachment.id}>
                              <img src={contextImageSrc(projectId, attachment.path)} alt={attachment.caption || attachment.filename}/>
                              <figcaption>{attachment.caption || attachment.filename}</figcaption>
                            </figure>))}
                        </div>)}
                      {isProcessing && (<div className="inline-suggestion-loading compact-loading">
                          <span /> IA en train de preparer la modification
                        </div>)}
                      {!!comment.replies?.length && (<div className="comment-thread">
                          {comment.replies.map((reply) => (<div key={reply.id}>
                              <strong>{reply.author === "user" ? "Vous" : "Agent"}</strong>
                              <span>{reply.content}</span>
                            </div>))}
                        </div>)}
                      {(comment.status === "open" || comment.status === "suggestion") && (<div className="comment-reply-form">
                            <textarea rows={2} value={commentReplies[comment.id] ?? ""} onChange={(event) => setCommentReplies((current) => ({ ...current, [comment.id]: event.target.value }))} placeholder="Repondre au commentaire"/>
                          <button className="button secondary compact-button" disabled={busy || isProcessing || !(commentReplies[comment.id] ?? "").trim()} onClick={() => submitCommentReply(comment.id)} type="button">
                            Repondre
                          </button>
                        </div>)}
                      {comment.edit_suggestion?.status === "failed" && (<div className="suggestion-error">{comment.edit_suggestion.error || "Suggestion impossible."}</div>)}
                      {comment.edit_suggestion?.status === "ready" && (<CommentSuggestionDiff suggestion={comment.edit_suggestion} projectId={projectId}/>)}
                      <div className="row">
                        <span className="comment-status">{comment.status}</span>
                        {(comment.status === "open" || comment.status === "suggestion") && (<>
                            {comment.section_id && (<button className="button secondary compact-button" type="button" onClick={() => focusReportComment(comment)}>
                                <ArrowUpRight size={15}/> Voir
                              </button>)}
                            {comment.edit_suggestion?.status === "ready" ? (<>
                                <button className="button compact-button" disabled={busy || isProcessing} onClick={() => acceptSuggestion(comment.id)}>
                                  <CheckCircle size={15}/> Accepter
                                </button>
                                <button className="button secondary compact-button" disabled={busy || isProcessing} onClick={() => rejectSuggestion(comment.id)}>
                                  Refuser
                                </button>
                              </>) : (<button className="button secondary compact-button" disabled={busy || isProcessing} onClick={() => suggestComment(comment.id)}>
                                <CheckCircle size={15}/> Traiter
                              </button>)}
                            <button className="button secondary icon-button" disabled={busy || isProcessing} onClick={() => dismissComment(comment.id)} title="Ignorer">
                              <X size={15}/>
                            </button>
                          </>)}
                      </div>
                    </li>);
                })}
                </ul>) : (<div className="empty-comments muted">
                    Active le mode commentaire, selectionne du texte ou survole une image pour demander une correction ciblee.
                  </div>)}
              </section>);

    const imagesToolContent = report ? (<ImageBankPanel report={report} analysis={analysis} projectId={projectId} selectedImage={selectedBankImage} onSelectImage={setSelectedBankImage} onDiscussImage={startBankImageDiscussion} assistantImages={reportInstructionBankImages} onToggleAssistantImage={toggleReportAssistantBankImage} onOpenAssistant={openReportAssistantComposer}/>) : (<section className="panel report-tool-empty">
                <h2>Banque d'images</h2>
                <p className="muted">La banque d'images apparaitra apres generation du rapport.</p>
              </section>);

    const reviewToolContent = (<section className="panel">
                <div className="section-heading">
                  <h2>Warnings relecture</h2>
                  <button disabled={busy || actionableReviewComments.length === 0} className="button secondary compact-button" onClick={() => applyOpenComments(null, actionableReviewComments.map((comment) => comment.id))} title="Envoyer les warnings et erreurs de relecture a l'agent">
                    <CheckCircle size={16}/> Traiter avec l'IA
                  </button>
                </div>
                {Number(factualCoverage.total ?? 0) > 0 && (<div className="factual-coverage-card">
                    <div>
                      <strong>Couverture factuelle</strong>
                      <span>{factualCoverage.total} claims matériels</span>
                    </div>
                    <div className="factual-coverage-grid">
                      <span className="verified">{factualCoverage.verified ?? 0}<small>vérifiés</small></span>
                      <span className="contradicted">{factualCoverage.contradicted ?? 0}<small>contredits</small></span>
                      <span className="unsupported">{factualCoverage.unsupported ?? 0}<small>non soutenus</small></span>
                      <span className="unavailable">{factualCoverage.unavailable ?? 0}<small>indisponibles</small></span>
                    </div>
                    {!!factualBatches.length && (<p>
                        {factualBatches.filter((batch) => batch.status === "completed").length} lot(s) terminé(s)
                        {factualBatches.some((batch) => batch.status === "split") ? " · lots trop larges redécoupés" : ""}
                      </p>)}
                  </div>)}
                {reviewIssues.length ? (<ul className="issue-list compact-list">
                  {reviewIssues.map((issue) => (<li key={issue.id} className={`${issue.severity}${issue.resolved ? " resolved" : ""}`}>
                      <strong>{issue.severity.toUpperCase()}</strong> {issue.section_id ? `${issue.section_id} - ` : ""}
                      {issue.message}
                    </li>))}
                </ul>) : (<div className="empty-comments muted">Aucun warning de relecture actif.</div>)}
              </section>);

    const deliveryToolContent = (<section className="panel">
                <h2>Livraison</h2>
                <div className="row">
                  <button disabled={busy || !report?.manifest.sections.length} className="button secondary compact-button" onClick={runFinalReview}>
                    <CheckCircle size={16}/> Relire
                  </button>
                  <button disabled={!canExport} className="button secondary compact-button" onClick={() => exportFile("pdf")} title={canExport ? "Exporter en PDF" : "Aucun contenu rédigé à exporter"}>
                    <Download size={16}/> PDF
                  </button>
                  <button disabled={!canExport} className="button secondary compact-button" onClick={() => exportFile("docx")} title={canExport ? "Exporter en DOCX" : "Aucun contenu rédigé à exporter"}>
                    <Download size={16}/> DOCX
                  </button>
                </div>
                {canExport && exportHasQualityWarnings && (<div className="export-blocker warning">
                    <strong>Export disponible avec points à vérifier</strong>
                    <span>Le PDF et le DOCX restent téléchargeables. Les questions, commentaires et warnings actifs seront aussi signalés après l'export.</span>
                  </div>)}
              </section>);

    const versionsToolContent = (<section className="panel report-version-tool">
                <div className="section-heading">
                  <h2>Versions</h2>
                  <span className="muted">{activeBranch?.name ?? "main"} - {sectionVersions.length} changement(s)</span>
                </div>
                <BranchPanel state={branchState} activeBranchId={activeBranchId} branchName={branchName} setBranchName={setBranchName} busy={busy || operationInProgress} mergeFeedback={mergeFeedback} onCreateBranch={createBranch} onSwitchBranch={switchBranch} onMergeBranch={mergeBranch} onClearMergeFeedback={() => setMergeFeedback(null)}/>
                <VersionPanel versions={sectionVersions} milestones={milestones} milestoneLabel={milestoneLabel} setMilestoneLabel={setMilestoneLabel} busy={busy} onCreateMilestone={createMilestone} onRestoreMilestone={restoreMilestone} onRestoreBlock={restoreBlockVersion} onRestoreSection={restoreSectionVersion} projectId={projectId} embedded/>
              </section>);

    const reportToolItems = [
                { id: "assistant", label: showPlanReview ? "Chat plan" : "Assistant", icon: <MessageSquare size={18}/>, badge: reportInstructionImages.length + reportInstructionBankImages.length, content: assistantToolContent },
                { id: "activity", label: "Activite IA", icon: <Wand2 size={18}/>, badge: jobInProgress ? 1 : 0, content: activityToolContent },
                { id: "images", label: "Banque d'images", icon: <ImagePlus size={18}/>, badge: report?.manifest.figures.length ?? 0, content: imagesToolContent },
                { id: "comments", label: "Commentaires", icon: <MessageSquare size={18}/>, badge: visibleComments.length, content: commentsToolContent },
                { id: "review", label: "Relecture", icon: <CheckCircle size={18}/>, badge: reviewIssues.length, content: reviewToolContent },
                { id: "delivery", label: "Livraison", icon: <Download size={18}/>, badge: hasBlockingReviewIssues ? 1 : 0, content: deliveryToolContent },
                { id: "versions", label: "Versions", icon: <History size={18}/>, badge: sectionVersions.length, content: versionsToolContent }
            ];

    const selectedReportTool = reportToolItems.find((tool) => tool.id === activeReportTool) ?? reportToolItems[0];

    const providerOptions = normalizeAiProviders(aiProviderInfo);

    const modeOptions = normalizeAiModes(aiProviderInfo);

    const activeProviderOption = providerOptions.find((option) => option.id === aiProvider);

    const activeAiMode = normalizeAiMode(aiMode, fastMode);

    const activeProviderModel = activeAiMode === "fast" && activeProviderOption?.fast_model
                ? activeProviderOption.fast_model
                : activeProviderOption?.model;

    const fastModeModel = activeProviderOption?.fast_model || fastModeInfo?.flash_model || "gpt-4.1-mini";

    const aiModeTitle = `Mode IA ${aiModeLabel(activeAiMode)}${activeAiMode === "fast" ? ` (${fastModeModel})` : ""}`;

    const backTarget = project.deepflow_project_id ? `/project/${project.deepflow_project_id}` : "/tools/guttenberg";

    const backLabel = project.deepflow_project_id ? "Projet" : "Rapports";

    return (<main className="guttenberg-root shell wide">
              <div className="topbar">
                <div>
                  <Link className="back-link" to={backTarget}>
                    <ArrowLeft size={16}/> {backLabel}
                  </Link>
                  <div className="brand">Innowide</div>
                  <h1>{titleDraft || project.title}</h1>
                  <div className="muted">
                    {project.client || "Client à renseigner"} - {project.report_type}
                  </div>
                </div>
                <div className="row">
                  <CollaborationStatus participants={otherParticipants} activeBranch={activeBranch} locks={effectiveCollaborationLocks}/>
                  <div className="ai-provider-control" title={`Provider IA actif : ${activeProviderOption?.label || aiProviderLabel(aiProvider)}${activeProviderModel ? ` (${activeProviderModel})` : ""}`}>
                    <Sparkles size={16}/>
                    <div className="segmented ai-provider-segmented" role="group" aria-label="Provider IA Guttenberg">
                      {providerOptions.map((option) => {
                        const unavailable = option.available === false;
                        const model = activeAiMode === "fast" && option.fast_model ? option.fast_model : option.model;
                        return (
                          <button
                            key={option.id}
                            className={option.id === aiProvider ? "active" : ""}
                            type="button"
                            disabled={aiProviderBusy || unavailable}
                            aria-pressed={option.id === aiProvider}
                            onClick={() => changeAiProvider(option.id)}
                            title={unavailable
                              ? `${option.label} non configure cote backend`
                              : option.description || `${option.label}${model ? ` - ${model}` : ""}`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="ai-mode-control" title={aiModeTitle}>
                    <Zap size={16}/>
                    <div className="segmented ai-mode-segmented" role="group" aria-label="Niveau de reflexion IA Guttenberg">
                      {modeOptions.map((option) => (
                        <button
                          key={option.id}
                          className={option.id === activeAiMode ? "active" : ""}
                          type="button"
                          disabled={fastModeBusy || option.available === false}
                          aria-pressed={option.id === activeAiMode}
                          onClick={() => changeAiMode(option.id)}
                          title={option.available === false
                            ? `${option.label} indisponible : cle OpenAI requise`
                            : option.id === "fast" ? `${option.title} (${fastModeModel})` : option.description || option.title}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button className="button secondary" onClick={() => load()} title="Rafraichir">
                    <RefreshCw size={17}/> Rafraichir
                  </button>
                  {browserNotificationPermission === "default" && (<button className="button secondary" type="button" onClick={enableBrowserQuestionNotifications} title="Recevoir les questions et changements d'étape quand cet onglet est en arrière-plan">
                    <Bell size={17}/> Alertes Guttenberg
                  </button>)}
                  <button className={`button secondary ${contextPanelOpen ? "active" : ""}`} type="button" onClick={() => setContextPanelOpen((current) => !current)} title="Questions, sources et informations du rapport">
                    <FileText size={17}/> Contexte
                    {(clarificationQuestions.length > 0 || project.context_integration_status === "pending") && <span className="context-button-badge">{clarificationQuestions.length || "!"}</span>}
                  </button>
                </div>
              </div>

              {contextPanelOpen && (<ReportContextPanel
                project={project}
                projectId={projectId}
                contextDraft={contextDraft}
                onContextChange={updateContextDraft}
                onSave={saveContextPanel}
                saving={contextSaving}
                uploadBusy={contextUploadBusy}
                onUploadFiles={uploadLiveContextFiles}
                pendingQuestions={clarificationQuestions}
                answeredQuestions={answeredClarificationQuestions}
                onOpenQuestion={setActiveClarificationQuestion}
                questionVisuals={questionVisuals}
                onClose={() => setContextPanelOpen(false)}
              />)}

              {inputAttentionCount > 0 && (<UserInputQueue
                comments={pendingValidationComments}
                clarificationQuestions={clarificationQuestions}
                showPlanReview={showPlanReview}
                onCommentClick={focusReportComment}
                onPlanClick={() => {
                    setMainTab("report");
                    setActiveReportTool("assistant");
                }}
                onQuestionClick={(question) => {
                    setMainTab("analysis");
                    setActiveClarificationQuestion(question);
                }}
              />)}

              {activeClarificationQuestion && (<ClarificationQuestionModal
                key={activeClarificationQuestion.id}
                question={activeClarificationQuestion}
                pendingCount={clarificationQuestions.length}
                questionPosition={Math.max(1, clarificationQuestions.findIndex((question) => question.id === activeClarificationQuestion.id) + 1)}
                hasNextQuestion={Boolean(nextClarificationQuestion(clarificationQuestions, activeClarificationQuestion.id))}
                busy={clarificationAnswerBusy || analysisRunning}
                projectId={projectId}
                referencedVisuals={questionVisuals(activeClarificationQuestion)}
                availableVisuals={(analysis?.visual_assets ?? []).filter((asset) => asset.path && asset.status !== "rejected" && asset.editorial_status !== "rejected" && asset.quality_status !== "rejected")}
                attachedSources={(project.source_files ?? []).filter((source) => (activeClarificationQuestion.source_file_ids ?? []).includes(source.id))}
                onAnswer={(answer, dismissed = false, files = [], visualAssetIds = []) => answerClarificationQuestion(activeClarificationQuestion, answer, dismissed, files, visualAssetIds)}
                onClose={() => setActiveClarificationQuestion(null)}
                onNext={() => {
                    setActiveClarificationQuestion(nextClarificationQuestion(
                        clarificationQuestions,
                        activeClarificationQuestion.id
                    ));
                }}
              />)}

              {clarificationQuestions.length > 0 && !activeClarificationQuestion && (<div className="clarification-inline">
                <div>
                  <strong>{clarificationQuestions[0]?.blocking
                        ? "Arbitrage requis pour reprendre l'analyse"
                        : clarificationQuestions.length > 1
                            ? `${clarificationQuestions.length} questions optionnelles`
                            : "Question optionnelle"}</strong>
                  <span>{clarificationQuestions[0]?.prompt}</span>
                </div>
                <button className="button secondary compact-button" type="button" onClick={() => setActiveClarificationQuestion(clarificationQuestions[0])}>
                  <MessageSquare size={16}/> Repondre
                </button>
              </div>)}

              <WorkflowStepper steps={workflowSteps} busy={busy} status={status} job={job} progress={progress} activityDetail={activityDetail} collapsed={workflowCollapsed} onToggleCollapsed={() => setWorkflowCollapsed((current) => !current)} onUploadFiles={uploadFiles} onCancel={cancelCurrentJob}/>

              <div className={`workspace ${mainTab === "report" ? `report-workspace ${reportOutlineCollapsed ? "outline-collapsed" : ""}` : mainTab === "analysis" ? "analysis-full" : ""}`}>
                {mainTab !== "analysis" && (<aside className={`grid ${mainTab === "report" ? `report-outline-aside ${reportOutlineCollapsed ? "collapsed" : ""}` : ""}`}>
                  {mainTab === "report" ? (<div className="report-outline-shell">
                      <button className="outline-toggle-button" type="button" onClick={() => setReportOutlineCollapsed((current) => !current)} title={reportOutlineCollapsed ? "Afficher le plan" : "Masquer le plan"}>
                        <FileText size={17}/>
                        {!reportOutlineCollapsed && <span>Plan</span>}
                      </button>
                      {!reportOutlineCollapsed && <ReportOutlinePanel report={report} sections={previewSections}/>}
                    </div>) : (<>
                      <section className="panel">
                        <div className="section-heading">
                          <h2>Brief du rapport</h2>
                          <button className="button secondary compact-button" type="button" disabled={busy} onClick={() => run("Enregistrement...", syncBrief)}>
                            Enregistrer
                          </button>
                        </div>
                        <div className="field">
                          <label htmlFor="report-title">Titre du rapport</label>
                          <input id="report-title" value={titleDraft} onChange={(event) => updateTitleDraft(event.target.value)} placeholder="Titre du rapport"/>
                        </div>
                        <div className="field">
                          <label htmlFor="target-pages">Longueur cible</label>
                          <input id="target-pages" value={targetPages} onChange={(event) => updateTargetPagesDraft(event.target.value)} placeholder="Optionnel, ex: 15"/>
                        </div>
                        <div className="field">
                          <label htmlFor="context">Objectif et contexte</label>
                          <textarea id="context" rows={6} value={contextDraft} onChange={(event) => updateContextDraft(event.target.value)}/>
                        </div>
                        <div className="client-logo-card">
                          <div className="section-heading">
                            <h3>Logo client</h3>
                            <span className="muted">{project.client_logo_path ? "Ajoute" : "Optionnel"}</span>
                          </div>
                          <div className="client-logo-preview">
                            {project.client_logo_path ? (<img src={reportImageSrc(projectId, project.client_logo_path)} alt={`Logo ${project.client || "client"}`}/>) : (<span>Aucun logo client</span>)}
                          </div>
                          <label className="dropzone row compact-dropzone">
                            <ImagePlus size={16}/>
                            <span>{project.client_logo_path ? "Remplacer le logo" : "Ajouter un logo"}</span>
                            <input hidden type="file" accept="image/*" onChange={uploadClientLogo}/>
                          </label>
                        </div>
                        <div className="muted">Commence par decrire ce que le rapport doit demontrer. L'analyse affinera ensuite le contexte et l'objectif.</div>
                      </section>

                      <section className="panel">
                        <h2>Chat projet</h2>
                        <div className="chat-log">
                          {projectChatMessages.map((message) => (<div className={`chat-message ${message.role}`} key={message.id}>
                              {message.content}
                            </div>))}
                          {analysisRunning && (<div className="chat-message agent live">
                              Analyse en cours...
                              {!!intakeEvents.length && (<ul className="intake-steps">
                                  {intakeEvents.slice(-5).map((item, index) => (<li key={`${item}-${index}`}>{item}</li>))}
                                </ul>)}
                            </div>)}
                          {!projectChatMessages.length && (<div className="muted">Tout passe par ce chat : objectif, contraintes, priorites, reponses aux questions.</div>)}
                        </div>
                        <form className="chat-form" onSubmit={sendChat}>
                          <input value={chatMessage} onChange={(event) => setChatMessage(event.target.value)} placeholder="Ajouter du contexte ou repondre a une question"/>
                          <button className="button secondary" type="submit" title="Envoyer">
                            <MessageSquare size={17}/>
                          </button>
                        </form>
                      </section>

                      <section className="panel compact-sources">
                        <div className="section-heading">
                          <h2>Sources</h2>
                          <span className="muted">{project.source_files.length} fichier(s)</span>
                        </div>
                        <label className="dropzone row compact-dropzone">
                          <Upload size={18}/>
                          <span>Ajouter des sources</span>
                          <input hidden type="file" multiple onChange={uploadFiles}/>
                        </label>
                        {project.deepflow_project_id && (<DriveSourcePicker driveFiles={driveFiles} driveLoading={driveLoading} driveSelection={driveSelection} driveBreadcrumbs={driveBreadcrumbs} driveQuery={driveQuery} selectedDriveCount={selectedDriveCount} busy={busy} onQueryChange={setDriveQuery} onSearch={searchDriveFiles} onClearSearch={clearDriveSearch} onRefresh={() => refreshDriveFiles()} onOpenFolder={openDriveFolder} onBreadcrumb={openDriveBreadcrumb} onToggle={toggleDriveFile} onImport={importSelectedDriveFiles} onLoadMore={() => refreshDriveFiles({ pageToken: driveFiles?.next_page_token, append: true })}/>)}
                        <ul className="file-list compact-file-list">
                          {project.source_files.map((file) => (<li key={file.id}>
                              {file.filename}
                              {sourcePathLabel(file) && sourcePathLabel(file) !== file.filename && (<div className="muted">{sourcePathLabel(file)}</div>)}
                              <div className="muted">{file.origin === "deepflow_drive" ? "Drive - " : ""}{sourceStatusLabel(file)}</div>
                            </li>))}
                        </ul>
                      </section>
                    </>)}

                </aside>)}

                <section className="report-surface">
                  <div className="report-toolbar">
                    <div>
                      <h2>{mainTab === "analysis" ? "Dossier d'analyse" : mainTab === "costs" ? "Couts IA" : "Rapport complet"}</h2>
                      <div className="muted">
                        {mainTab === "analysis"
                    ? "Tri des images, constats et données exploitées par le rapport."
                    : mainTab === "costs"
                        ? "Suivi estime des tokens et depenses OpenAI par projet."
                        : "La preview se remplit au fur et a mesure que les agents terminent leurs sections."}
                      </div>
                    </div>
                    <div className="toolbar-actions">
                      <div className="segmented">
                        <button className={mainTab === "analysis" ? "active" : ""} onClick={() => setMainTab("analysis")}>Analyse</button>
                        <button className={mainTab === "report" ? "active" : ""} onClick={() => setMainTab("report")}>Rapport</button>
                        <button className={mainTab === "costs" ? "active" : ""} onClick={() => setMainTab("costs")}>Couts</button>
                      </div>
                      {mainTab === "report" && (<>
                          {report?.manifest && (<ReportAttributionToggle
                            visible={report.manifest.show_guttenberg_attribution !== false}
                            saving={attributionSaving}
                            onToggle={toggleGuttenbergAttribution}
                          />)}
                          <button className={`button secondary ${editMode ? "active" : ""}`} onClick={() => {
                        setEditMode((current) => !current);
                        setCommentMode(false);
                        setCommentAnchor(null);
                    }} title="Modifier le texte directement dans la preview">
                            <Pencil size={17}/> Edition
                          </button>
                          <button className={`button secondary ${commentMode ? "active" : ""}`} onClick={() => {
                        setCommentMode((current) => !current);
                        setEditMode(false);
                    }} title="Activer le mode commentaire">
                            <MessageSquare size={17}/> Commentaire
                          </button>
                        </>)}
                    </div>
                  </div>
                  {mainTab === "report" && commentAnchor && !pendingComment && (<button className="floating-comment-button" style={{ top: commentAnchor.top, left: commentAnchor.left }} onMouseDown={(event) => event.preventDefault()} onClick={startAnchoredComment} title="Commenter cette selection">
                      <MessageSquare size={18}/>
                    </button>)}
                  {mainTab === "analysis" ? (<AnalysisPanel analysis={analysis} draft={analysisDraft} setDraft={updateAnalysisDraft} onSave={saveAnalysis} onVisualStatusChange={updateVisualAssetStatus} onVisualLabelChange={updateVisualAssetLabel} projectId={projectId} project={project} busy={busy} visualEditingLocked={visualEditingLocked} report={report} titleDraft={titleDraft} onTitleChange={updateTitleDraft} targetPages={targetPages} onTargetPagesChange={updateTargetPagesDraft} contextDraft={contextDraft} onContextChange={updateContextDraft} onSaveBrief={() => run("Enregistrement...", syncBrief)} onUploadFiles={uploadFiles} onUploadClientLogo={uploadClientLogo} showDrive={Boolean(project.deepflow_project_id)} driveProps={{ driveFiles, driveLoading, driveSelection, driveBreadcrumbs, driveQuery, selectedDriveCount, busy, onQueryChange: setDriveQuery, onSearch: searchDriveFiles, onClearSearch: clearDriveSearch, onRefresh: () => refreshDriveFiles(), onOpenFolder: openDriveFolder, onBreadcrumb: openDriveBreadcrumb, onToggle: toggleDriveFile, onImport: importSelectedDriveFiles, onLoadMore: () => refreshDriveFiles({ pageToken: driveFiles?.next_page_token, append: true }) }} onPrepareVisuals={() => prepareVisuals()} visualPreparationRunning={visualPreparationRunning} visualInventoryReady={visualInventoryReady} visualInventoryStale={visualInventoryStale} onAnalyze={() => analyzeProject()} onResetAnalysis={resetAnalysis} analysisRunning={analysisStepRunning} hasAnalysisArtifacts={hasAnalysisArtifacts} operationInProgress={operationInProgress} intakeEvents={intakeEvents} job={job} progress={progress} status={status} onCancel={cancelCurrentJob} chatMessages={projectChatMessages} chatMessage={chatMessage} onChatMessageChange={setChatMessage} onSendChat={sendChat}/>) : mainTab === "costs" ? (<CostPanel costs={costs}/>) : showOutlinePreview ? (<div className="preview-editor single">
                      <OutlineLivePreview title={project.title} sections={outlinePreview} running={outlineRunning} raw={outlineDraft}/>
                    </div>) : showPlanReview ? (<div className="preview-editor single">
                      <PlanReviewPanel title={project.title} sections={outlineReviewSections} instruction={outlineInstruction} setInstruction={setOutlineInstruction} onRefine={refineOutline} onApprove={approveOutline} busy={busy || outlineRunning} approving={outlineApprovalPending}/>
                    </div>) : report?.manifest.sections.length ? (<div className="preview-editor single">
                      <FullReportPreview report={report} sections={previewSections} projectId={projectId} commentMode={commentMode} editMode={editMode} comments={comments} sectionVersions={sectionVersions} activeVersion={activeVersion} assistantVersionIds={assistantReviewVersionIds} processingCommentIds={processingCommentIds} processingImageKeys={processingImageKeys} processingSectionIds={processingSectionIds} savingSectionIds={savingSectionIds} selectedBankImage={selectedBankImage} collaborationLocks={effectiveCollaborationLocks} currentSessionId={currentSessionId} onTextSelection={updateTextCommentAnchor} onImageSelection={selectImageForComment} onImageEditSelection={selectImageForEdit} onChartComment={selectChartForComment} onImageAutoRetouch={autoRetouchImage} onInsertionPoint={startInsertionComment} onImageReplaceUpload={replaceImageWithUpload} onImageReplaceWithBank={replaceImageWithBank} onImageCrop={openImageCropEditor} onAcceptSuggestion={acceptSuggestion} onRejectSuggestion={rejectSuggestion} onActiveVersionChange={setActiveVersion} onRestoreBlock={restoreBlockVersion} onRestoreSection={restoreSectionVersion} onBlockEdit={savePreviewBlock} onInsertBlock={insertPreviewBlock} onDeleteBlock={deletePreviewBlock} onCreateSection={createReportSection} onDeleteSection={deleteReportSection} onEditFocus={focusEditableBlock} onEditBlur={blurEditableBlock}/>
                    </div>) : (<div className="panel muted">Le rapport complet apparaitra ici pendant la génération.</div>)}
                </section>
                {mainTab === "report" && (<aside className="workspace-right report-tools-panel">
                    <nav className="report-tool-rail" aria-label="Outils du rapport">
                      {reportToolItems.map((tool) => (<button className={`report-tool-button ${selectedReportTool.id === tool.id ? "active" : ""}`} type="button" key={tool.id} onClick={() => setActiveReportTool(tool.id)} title={tool.label} aria-label={tool.label}>
                          {tool.icon}
                          {!!tool.badge && <span>{tool.badge > 99 ? "99+" : tool.badge}</span>}
                        </button>))}
                    </nav>
                    <div className="report-tool-content">
                      {selectedReportTool.content}
                    </div>
                  </aside>)}
              </div>
              {pendingComment && (<form className="comment-composer" style={{ top: pendingComment.top, left: pendingComment.left }} onSubmit={submitComment}>
                  <div>
                    <strong>
                      {pendingComment.targetType === "image_edit"
                        ? "Retouche image"
                        : pendingComment.targetType === "image"
                            ? "Commentaire sur image"
                            : pendingComment.targetType === "chart"
                                ? "Retouche graphe"
                                : pendingComment.targetType === "insertion"
                                    ? "Insertion"
                                    : "Commentaire sur texte"}
                    </strong>
                    <div className="muted">
                      Section {pendingComment.sectionId}
                      {pendingComment.targetType === "insertion" ? " - insertion" : ""}
                      {pendingComment.targetType === "chart"
                        ? ` - ${pendingComment.contextBefore || "graphe cible"}`
                        : pendingComment.selectedText
                            ? ` - "${pendingComment.selectedText.slice(0, 110)}"`
                            : ""}
                      {pendingComment.imagePath ? ` - ${pendingComment.imagePath}` : ""}
                    </div>
                  </div>
                  <textarea autoFocus rows={4} value={commentDraft} onChange={(event) => setCommentDraft(event.target.value)} placeholder={pendingComment.targetType === "image_edit"
                        ? "Ex: recadrer, nettoyer le fond, améliorer le contraste, rendre les annotations lisibles..."
                        : pendingComment.targetType === "chart"
                            ? "Ex: retirer le premier point, changer l'axe Y, renommer une serie, masquer les points..."
                            : "Modification demandee"}/>
                  <div className="manual-image-insert">
                    {selectedBankImage && pendingComment.targetType === "insertion" && (<div className="selected-bank-image">
                        <img src={reportImageSrc(projectId, selectedBankImage.path)} alt={selectedBankImage.caption}/>
                        <div>
                          <strong>{selectedBankImage.caption || selectedBankImage.path}</strong>
                          <span>{selectedBankImage.path}</span>
                        </div>
                        <button className="button secondary compact-button" type="button" onClick={insertSelectedBankImageFromComposer} disabled={busy || imageComposerSaving}>
                          Inserer la selection
                        </button>
                      </div>)}
                    <label className="dropzone compact-dropzone manual-image-dropzone">
                      <ImagePlus size={16}/>
                      <span>
                        {manualImageFile
                        ? manualImageFile.name
                        : pendingComment.targetType === "insertion"
                            ? "Choisir une image a inserer ici ou a fournir a l'IA"
                            : "Joindre une image pour l'IA"}
                      </span>
                      <input hidden type="file" accept="image/*" onChange={(event) => {
                        const file = event.target.files?.[0] ?? null;
                        setManualImageFile(file);
                        setManualImageCaption(file ? defaultImageCaption(file) : "");
                    }}/>
                    </label>
                    {manualImageFile && (<>
                        <input value={manualImageCaption} onChange={(event) => setManualImageCaption(event.target.value)} placeholder="Legende de l'image"/>
                        <textarea rows={2} value={manualImageNote} onChange={(event) => setManualImageNote(event.target.value)} placeholder="Note optionnelle pour les IA sur ce que montre l'image"/>
                        {pendingComment.targetType === "insertion" && (<button className="button secondary compact-button" type="button" onClick={insertManualImageFromComposer} disabled={busy || imageComposerSaving}>
                            <ImagePlus size={15}/> Inserer l'image
                          </button>)}
                      </>)}
                  </div>
                  <div className="row">
                    <button className="button" type="submit" disabled={!commentDraft.trim() || busy}>
                      Ajouter
                    </button>
                    <button className="button secondary" type="button" onClick={submitAndTreatComment} disabled={!commentDraft.trim() || busy}>
                      Traiter
                    </button>
                    <button className="button secondary" type="button" onClick={() => { setPendingComment(null); resetManualImageDraft(); }}>
                      Annuler
                    </button>
                  </div>
                </form>)}
              {imageEditTarget && (<ImageCropModal caption={imageEditTarget.caption} imagePath={imageEditTarget.imagePath} projectId={projectId} busy={imageCropBusy} onApply={applyImageCrop} onClose={() => setImageEditTarget(null)}/>)}
            </main>);
}
